import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Container,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import LaunchIcon from "@material-ui/icons/Launch";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { getInvoicesByUser } from "../actions/invoiceActions";
import {
  createOutstandingBalance,
  deleteOutstandingBalance,
  fetchOutstandingBalances,
} from "../api";

const currencyFmt = (value, code = "USD") =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const dateFmt = (value) => (value ? new Date(value).toLocaleDateString() : "No date");

const computeOutstanding = (invoice = {}) => {
  const total = Number(invoice.total ?? 0);
  const explicitReceived = Number(invoice.totalAmountReceived ?? 0);
  if (explicitReceived) return Math.max(total - explicitReceived, 0);
  const payments = Array.isArray(invoice.paymentRecords)
    ? invoice.paymentRecords.reduce((sum, rec) => sum + Number(rec?.amountPaid || 0), 0)
    : 0;
  return Math.max(total - payments, 0);
};

const normalizeAmount = (value) => {
  const parsed = Number(String(value ?? "").replace(/[$,]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const differenceColor = (value) => (Number(value) >= 0 ? "#047857" : "#b45309");

const normalizeAdditionalEntries = (entries) =>
  Array.isArray(entries) ? entries.filter((entry) => normalizeAmount(entry?.amount) > 0) : [];

const totalAdditionalBalances = (entries) =>
  normalizeAdditionalEntries(entries).reduce((sum, entry) => sum + normalizeAmount(entry.amount), 0);

const getBalanceId = (entry = {}) => entry._id || entry.id;

const isReceivedByToday = (invoice = {}) => {
  if (!invoice.receivedByDate) return false;
  const receivedDate = new Date(invoice.receivedByDate);
  if (Number.isNaN(receivedDate.getTime())) return false;
  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);
  return receivedDate <= todayEnd;
};

export default function OutstandingInvoicesPage() {
  const history = useHistory();
  const dispatch = useDispatch();
  const { invoices = [], isLoading = false } = useSelector((state) => state?.invoices || {});
  const [query, setQuery] = React.useState("");
  const [savedAmounts, setSavedAmounts] = React.useState({});
  const [additionalBalances, setAdditionalBalances] = React.useState({});
  const [balanceDrafts, setBalanceDrafts] = React.useState({});
  const [expandedCustomers, setExpandedCustomers] = React.useState({});
  const [balancesLoading, setBalancesLoading] = React.useState(false);

  const userInfo = React.useMemo(() => {
    if (typeof window === "undefined") return null;
    try {
      const stored = JSON.parse(localStorage.getItem("profile") || "null");
      return stored?.result || stored;
    } catch {
      return null;
    }
  }, []);

  const storageKey = React.useMemo(() => {
    const userKey = userInfo?._id || userInfo?.googleId || userInfo?.email || "anonymous";
    return `outstandingCustomerAmounts:${userKey}`;
  }, [userInfo]);

  React.useEffect(() => {
    const id = userInfo?._id || userInfo?.googleId;
    if (!id || invoices?.length) return;
    dispatch(getInvoicesByUser({ search: id }));
  }, [dispatch, invoices?.length, userInfo]);

  React.useEffect(() => {
    try {
      setSavedAmounts(JSON.parse(localStorage.getItem(storageKey) || "{}"));
    } catch {
      setSavedAmounts({});
    }
  }, [storageKey]);

  const saveAmount = (customer, value) => {
    setSavedAmounts((prev) => {
      const next = { ...prev, [customer]: value };
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  };

  React.useEffect(() => {
    const userId = userInfo?._id || userInfo?.googleId;
    if (!userId) return;

    let cancelled = false;
    setBalancesLoading(true);
    fetchOutstandingBalances({ search: userId })
      .then(({ data }) => {
        if (cancelled) return;
        const grouped = {};
        (data?.data || []).forEach((entry) => {
          const customer = entry?.customer || "Unknown customer";
          grouped[customer] = [...(grouped[customer] || []), entry];
        });
        setAdditionalBalances(grouped);
      })
      .catch((error) => {
        console.log(error?.response || error);
        if (!cancelled) setAdditionalBalances({});
      })
      .finally(() => {
        if (!cancelled) setBalancesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userInfo]);

  const toggleExpanded = (customer) => {
    setExpandedCustomers((prev) => ({ ...prev, [customer]: !prev[customer] }));
  };

  const setDraftValue = (customer, field, value) => {
    setBalanceDrafts((prev) => ({
      ...prev,
      [customer]: {
        ...(prev[customer] || {}),
        [field]: value,
      },
    }));
  };

  const addAdditionalBalance = async (customer) => {
    const draft = balanceDrafts[customer] || {};
    const amount = normalizeAmount(draft.amount);
    const userId = userInfo?._id || userInfo?.googleId;
    if (!userId) return;
    if (amount <= 0) return;

    try {
      const { data: entry } = await createOutstandingBalance({
        userId,
        customer,
        amount,
        note: draft.note || "",
      });
      setAdditionalBalances((prev) => ({
        ...prev,
        [customer]: [entry, ...normalizeAdditionalEntries(prev[customer])],
      }));
      setBalanceDrafts((prev) => ({ ...prev, [customer]: { amount: "", note: "" } }));
      setExpandedCustomers((prev) => ({ ...prev, [customer]: true }));
    } catch (error) {
      console.log(error?.response || error);
    }
  };

  const removeAdditionalBalance = async (customer, entryId) => {
    if (!entryId) return;
    try {
      await deleteOutstandingBalance(entryId);
      setAdditionalBalances((prev) => {
        const nextEntries = normalizeAdditionalEntries(prev[customer]).filter((entry) => getBalanceId(entry) !== entryId);
        const next = { ...prev };
        if (nextEntries.length) {
          next[customer] = nextEntries;
        } else {
          delete next[customer];
        }
        return next;
      });
    } catch (error) {
      console.log(error?.response || error);
    }
  };

  const groups = React.useMemo(() => {
    const grouped = new Map();
    (invoices || []).forEach((invoice) => {
      if (!isReceivedByToday(invoice)) return;
      const amountDue = computeOutstanding(invoice);
      if (amountDue <= 0) return;
      const status = String(invoice?.status || "").toLowerCase();
      if (!["unpaid", "partial", "pending"].includes(status)) return;

      const customer =
        invoice?.billTo?.name ||
        invoice?.client?.store ||
        invoice?.client?.name ||
        "Unknown customer";
      if (!grouped.has(customer)) {
        grouped.set(customer, { customer, total: 0, invoices: [] });
      }
      const group = grouped.get(customer);
      group.total += amountDue;
      group.invoices.push({
        id: invoice._id,
        invoiceNumber: invoice.invoiceNumber,
        receivedByDate: invoice.receivedByDate,
        dueDate: invoice.dueDate,
        amountDue,
      });
    });

    Object.entries(additionalBalances || {}).forEach(([customer, entries]) => {
      if (totalAdditionalBalances(entries) <= 0) return;
      if (!grouped.has(customer)) {
        grouped.set(customer, { customer, total: 0, invoices: [] });
      }
    });

    return Array.from(grouped.values())
      .map((group) => ({
        ...group,
        additionalBalances: normalizeAdditionalEntries(additionalBalances[group.customer]),
        additionalTotal: totalAdditionalBalances(additionalBalances[group.customer]),
        invoices: group.invoices.sort(
          (a, b) => new Date(a.dueDate || 0) - new Date(b.dueDate || 0)
        ),
      }))
      .map((group) => ({
        ...group,
        totalDue: group.total + group.additionalTotal,
      }))
      .filter((group) => group.totalDue > 0)
      .filter((group) => group.customer.toLowerCase().includes(query.trim().toLowerCase()))
      .sort((a, b) => b.totalDue - a.totalDue);
  }, [invoices, query, additionalBalances]);

  const totals = React.useMemo(() => {
    const outstanding = groups.reduce((sum, group) => sum + group.total, 0);
    const additional = groups.reduce((sum, group) => sum + group.additionalTotal, 0);
    const totalDue = outstanding + additional;
    const saved = groups.reduce((sum, group) => sum + normalizeAmount(savedAmounts[group.customer]), 0);
    const invoiceCount = groups.reduce((sum, group) => sum + group.invoices.length, 0);
    return { outstanding, additional, saved, invoiceCount, totalDue, difference: saved - totalDue };
  }, [groups, savedAmounts]);

  return (
    <Container maxWidth="lg" style={{ paddingTop: 24, paddingBottom: 80 }}>
      <Box mb={3}>
        <Typography variant="h4" style={{ fontWeight: 700 }}>
          Outstanding Invoices by Customer
        </Typography>
      </Box>

      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="textSecondary">Outstanding</Typography>
              <Typography variant="h6">{currencyFmt(totals.outstanding)}</Typography>
            </CardContent>
          </Card>
        </Grid>
	        <Grid item xs={12} sm={6} md={2}>
	          <Card variant="outlined">
	            <CardContent>
	              <Typography variant="body2" color="textSecondary">Additional Balances</Typography>
	              <Typography variant="h6">{currencyFmt(totals.additional)}</Typography>
	            </CardContent>
	          </Card>
	        </Grid>
	        <Grid item xs={12} sm={6} md={2}>
	          <Card variant="outlined">
	            <CardContent>
	              <Typography variant="body2" color="textSecondary">Total Due</Typography>
	              <Typography variant="h6" style={{ color: "#b45309" }}>
	                {currencyFmt(totals.totalDue)}
	              </Typography>
	            </CardContent>
	          </Card>
	        </Grid>
	        <Grid item xs={12} sm={6} md={2}>
	          <Card variant="outlined">
	            <CardContent>
	              <Typography variant="body2" color="textSecondary">Saved Amounts</Typography>
	              <Typography variant="h6">{currencyFmt(totals.saved)}</Typography>
	            </CardContent>
	          </Card>
	        </Grid>
	        <Grid item xs={12} sm={6} md={2}>
	          <Card variant="outlined">
	            <CardContent>
	              <Typography variant="body2" color="textSecondary">Difference</Typography>
	              <Typography variant="h6" style={{ color: differenceColor(totals.difference) }}>
	                {currencyFmt(totals.difference)}
	              </Typography>
	            </CardContent>
	          </Card>
	        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="textSecondary">Invoices</Typography>
              <Typography variant="h6">{totals.invoiceCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box mb={2}>
        <TextField
          label="Filter customers"
          variant="outlined"
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          fullWidth
        />
      </Box>

      {isLoading || balancesLoading ? (
        <Typography>Loading invoices...</Typography>
	      ) : groups.length === 0 ? (
	        <Typography>No outstanding invoices found.</Typography>
	      ) : (
	        <TableContainer component={Paper} variant="outlined">
	          <Table size="small">
	            <TableHead>
	              <TableRow>
	                <TableCell style={{ width: 48 }} />
	                <TableCell>Customer</TableCell>
	                <TableCell align="right">Invoices</TableCell>
	                <TableCell align="right">Invoice Balance</TableCell>
	                <TableCell align="right">Additional Balances</TableCell>
	                <TableCell align="right">Total Due</TableCell>
	                <TableCell align="right">Saved Amount</TableCell>
	                <TableCell align="right">Difference</TableCell>
	                <TableCell align="right" style={{ width: 88 }}>Add</TableCell>
	              </TableRow>
	            </TableHead>
	            <TableBody>
	              {groups.map((group) => {
	                const expanded = Boolean(expandedCustomers[group.customer]);
	                const draft = balanceDrafts[group.customer] || {};
	                const savedValue = savedAmounts[group.customer] || "";
	                const savedNumber = normalizeAmount(savedValue);
	                const difference = savedValue === "" ? null : savedNumber - group.totalDue;
	                return (
	                  <React.Fragment key={group.customer}>
	                    <TableRow
	                      hover
	                      onClick={() => toggleExpanded(group.customer)}
	                      style={{ cursor: "pointer" }}
	                    >
	                      <TableCell padding="checkbox">
	                        <IconButton size="small" aria-label={expanded ? "Collapse customer" : "Expand customer"}>
	                          {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
	                        </IconButton>
	                      </TableCell>
	                      <TableCell>
	                        <Typography variant="body2" style={{ fontWeight: 700 }}>
	                          {group.customer}
	                        </Typography>
	                      </TableCell>
	                      <TableCell align="right">{group.invoices.length}</TableCell>
	                      <TableCell align="right">{currencyFmt(group.total)}</TableCell>
	                      <TableCell align="right">{currencyFmt(group.additionalTotal)}</TableCell>
	                      <TableCell align="right" style={{ fontWeight: 700 }}>
	                        {currencyFmt(group.totalDue)}
	                      </TableCell>
	                      <TableCell align="right" onClick={(event) => event.stopPropagation()}>
	                        <TextField
	                          label="Saved"
	                          type="number"
	                          variant="outlined"
	                          size="small"
	                          value={savedValue}
	                          onChange={(event) => saveAmount(group.customer, event.target.value)}
	                          style={{ width: 130 }}
	                        />
	                      </TableCell>
	                      <TableCell align="right">
	                        <Typography
	                          variant="body2"
	                          style={{ fontWeight: 700, color: difference === null ? "#047857" : differenceColor(difference) }}
	                        >
	                          {difference === null ? "-" : currencyFmt(difference)}
	                        </Typography>
	                      </TableCell>
	                      <TableCell align="right">
	                        <IconButton
	                          size="small"
	                          color="primary"
	                          aria-label={`Add additional balance for ${group.customer}`}
	                          onClick={(event) => {
	                            event.stopPropagation();
	                            setExpandedCustomers((prev) => ({ ...prev, [group.customer]: true }));
	                          }}
	                        >
	                          <AddCircleOutlineIcon fontSize="small" />
	                        </IconButton>
	                      </TableCell>
	                    </TableRow>
	                    <TableRow>
	                      <TableCell colSpan={9} style={{ padding: 0, borderBottom: expanded ? "1px solid #e5e7eb" : 0 }}>
	                        <Collapse in={expanded} timeout="auto" unmountOnExit>
	                          <Box p={2} style={{ background: "#f9fafb" }}>
	                            <Grid container spacing={2}>
	                              <Grid item xs={12} md={7}>
	                                <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
	                                  Invoices
	                                </Typography>
	                                {group.invoices.length === 0 ? (
	                                  <Typography variant="body2" color="textSecondary">
	                                    No invoice balances. This customer only has additional balances.
	                                  </Typography>
	                                ) : (
	                                  <Table size="small">
	                                    <TableBody>
	                                      {group.invoices.map((invoice) => (
	                                        <TableRow key={invoice.id}>
	                                          <TableCell>
	                                            <Typography variant="body2" style={{ fontWeight: 700 }}>
	                                              Invoice #{invoice.invoiceNumber || invoice.id}
	                                            </Typography>
	                                            <Typography variant="caption" color="textSecondary">
	                                              Ship {dateFmt(invoice.receivedByDate)} - Due {dateFmt(invoice.dueDate)}
	                                            </Typography>
	                                          </TableCell>
	                                          <TableCell align="right">{currencyFmt(invoice.amountDue)}</TableCell>
	                                          <TableCell align="right">
	                                            <Button
	                                              size="small"
	                                              color="primary"
	                                              onClick={() => history.push(`/invoice/${invoice.id}`)}
	                                              startIcon={<LaunchIcon />}
	                                            >
	                                              Open
	                                            </Button>
	                                          </TableCell>
	                                        </TableRow>
	                                      ))}
	                                    </TableBody>
	                                  </Table>
	                                )}
	                              </Grid>
	                              <Grid item xs={12} md={5}>
	                                <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
	                                  Additional balances
	                                </Typography>
	                                {group.additionalBalances.length === 0 ? (
	                                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
	                                    No additional balances added.
	                                  </Typography>
	                                ) : (
	                                  <Box mb={1}>
	                                    {group.additionalBalances.map((entry) => (
	                                      <Box
	                                        key={getBalanceId(entry)}
	                                        display="flex"
	                                        alignItems="center"
	                                        justifyContent="space-between"
	                                        py={0.5}
	                                        style={{ gap: 8, borderBottom: "1px solid #e5e7eb" }}
	                                      >
	                                        <Box>
	                                          <Typography variant="body2" style={{ fontWeight: 700 }}>
	                                            {currencyFmt(entry.amount)}
	                                          </Typography>
	                                          <Typography variant="caption" color="textSecondary">
	                                            {entry.note || "Additional balance"} - {dateFmt(entry.createdAt)}
	                                          </Typography>
	                                        </Box>
	                                        <IconButton
	                                          size="small"
	                                          aria-label="Remove additional balance"
	                                          onClick={() => removeAdditionalBalance(group.customer, getBalanceId(entry))}
	                                        >
	                                          <DeleteOutlineIcon fontSize="small" />
	                                        </IconButton>
	                                      </Box>
	                                    ))}
	                                  </Box>
	                                )}
	                                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
	                                  <TextField
	                                    label="Amount"
	                                    type="number"
	                                    variant="outlined"
	                                    size="small"
	                                    value={draft.amount || ""}
	                                    onChange={(event) => setDraftValue(group.customer, "amount", event.target.value)}
	                                    style={{ maxWidth: 140 }}
	                                  />
	                                  <TextField
	                                    label="Note"
	                                    variant="outlined"
	                                    size="small"
	                                    value={draft.note || ""}
	                                    onChange={(event) => setDraftValue(group.customer, "note", event.target.value)}
	                                    fullWidth
	                                  />
	                                  <IconButton
	                                    color="primary"
	                                    aria-label="Add additional balance"
	                                    onClick={() => addAdditionalBalance(group.customer)}
	                                  >
	                                    <AddCircleOutlineIcon />
	                                  </IconButton>
	                                </Box>
	                              </Grid>
	                            </Grid>
	                          </Box>
	                        </Collapse>
	                      </TableCell>
	                    </TableRow>
	                  </React.Fragment>
	                );
	              })}
	            </TableBody>
	          </Table>
	        </TableContainer>
	      )}
    </Container>
  );
}
